import { Flex, Text } from "@mantine/core";
import {
    MantineReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_RowVirtualizer,
    type MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import { type UIEvent, useCallback, useEffect, useRef, useState } from "react";
import TopToolbarCustomActions from "../TopToolbarCustomActions";
import { getRowId, getTotalDBRowCount, validateDateRange } from "./utils";

interface MetadataDocumentsTableProps {
    index: string;
    // biome-ignore lint/suspicious/noExplicitAny: API response structure is dynamic
    columns: MRT_ColumnDef<any>[];
    // biome-ignore lint/suspicious/noExplicitAny: API response structure is dynamic
    data: any[];
    // biome-ignore lint/suspicious/noExplicitAny: API response structure is dynamic
    queryData: any;
    isLoading: boolean;
    isFetching: boolean;
    sorting: MRT_SortingState;
    setSorting: (sorting: MRT_SortingState) => void;
    columnFilters: MRT_ColumnFiltersState;
    setColumnFilters: (
        filters:
            | MRT_ColumnFiltersState
            | ((prev: MRT_ColumnFiltersState) => MRT_ColumnFiltersState),
    ) => void;
    globalFilter?: string;
    setGlobalFilter: (filter?: string) => void;
    fetchNextPage: () => void;
}

export const MetadataDocumentsTable = ({
    index,
    columns,
    data,
    queryData,
    isLoading,
    isFetching,
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters,
    globalFilter: _globalFilter,
    setGlobalFilter,
    fetchNextPage,
}: MetadataDocumentsTableProps) => {
    const rowVirtualizerInstanceRef =
        useRef<MRT_RowVirtualizer<HTMLDivElement, HTMLTableRowElement>>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [rowVirtualizerRef, setRowVirtualizerRef] = useState<MRT_RowVirtualizer<
        HTMLDivElement,
        HTMLTableRowElement
    > | null>(null);

    const totalDBRowCount = getTotalDBRowCount(queryData);

    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
                if (
                    scrollHeight - scrollTop - clientHeight < 400 &&
                    !isFetching &&
                    data.length < totalDBRowCount
                ) {
                    fetchNextPage();
                }
            }
        },
        [fetchNextPage, isFetching, data.length, totalDBRowCount],
    );

    const handleColumnFiltersChange = (
        updater:
            | MRT_ColumnFiltersState
            | ((prev: MRT_ColumnFiltersState) => MRT_ColumnFiltersState),
    ) => {
        const newFilters = typeof updater === "function" ? updater(columnFilters) : updater;

        if (validateDateRange(newFilters)) {
            setColumnFilters(newFilters);
        }
    };

    useEffect(() => {
        fetchMoreOnBottomReached(tableContainerRef.current);
    }, [fetchMoreOnBottomReached]);

    useEffect(() => {
        if (rowVirtualizerRef) {
            rowVirtualizerRef.scrollToIndex(0);
        }
    }, [rowVirtualizerRef]);

    // Reset scroll position when index changes
    useEffect(() => {
        if (rowVirtualizerInstanceRef.current) {
            try {
                rowVirtualizerInstanceRef.current.scrollToIndex(0);
            } catch (e) {
                console.error(e);
            }
        }
    }, []);

    const table = useMantineReactTable({
        layoutMode: "grid",
        columns,
        data: data ?? [],
        enableColumnOrdering: true,
        enablePinning: true,
        enableBottomToolbar: false,
        renderToolbarAlertBannerContent: () => <></>,
        enableColumnResizing: true,
        enableColumnFilterModes: false,
        enableGlobalFilter: false,
        enableFullScreenToggle: false,
        // TODO This used to specify a filter function, but this property doesn't actually exist.
        // It should be converted into using `filterFns`.
        // filterFn: "includesString",
        enablePagination: false,
        enableRowNumbers: true,
        enableClickToCopy: true,
        getRowId: (row) => getRowId(row),
        mantineTableHeadProps: {
            id: "documents-table",
        },
        renderTopToolbarCustomActions: () => (
            <>
                <TopToolbarCustomActions
                    // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                    table={table as any}
                    index={index}
                    data={queryData}
                    columnFilters={columnFilters}
                    isLoading={false}
                />
                <Flex align="center" gap="md">
                    <Text size="sm" c="dimmed">
                        {`Total: ${totalDBRowCount}, (Showing ${data.length})`}
                    </Text>
                </Flex>
            </>
        ),
        mantineToolbarAlertBannerProps: {
            style: {
                display: "none",
                background: "#f0f0f0",
                fontWeight: "bold",
            },
        },
        onColumnFiltersChange: handleColumnFiltersChange,
        onGlobalFilterChange: setGlobalFilter,
        enableSelectAll: false,
        selectAllMode: "page",
        enableGrouping: true,
        enableRowVirtualization: true,
        enableDensityToggle: false,
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: {
                padding: 0,
                borderRadius: 0,
                maxHeight: "calc(100vh - 300px)",
                overflowX: "auto",
            },
            onScroll: (event: UIEvent<HTMLDivElement>) =>
                fetchMoreOnBottomReached(event.target as HTMLDivElement),
        },
        mantinePaperProps: {
            style: {
                borderRadius: 0,
                zIndex: 102,
                border: "none",
                boxShadow: "none",
            },
        },
        mantineTableProps: {
            style: {
                "& thead tr th": {
                    borderTop: "none !important",
                    borderLeft: "none !important",
                    borderRight: "none !important",
                    borderBottom: "1px solid var(--mantine-color-gray-3) !important",
                },
                "& tbody tr td": {
                    borderLeft: "none !important",
                    borderRight: "none !important",
                },
            },
        },
        onSortingChange: setSorting,
        state: { isLoading, sorting },
        initialState: {
            showGlobalFilter: true,
            density: "xs",
            showColumnFilters: true,
        },
        rowVirtualizerInstanceRef: ((instance) => {
            setRowVirtualizerRef(instance);
            // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        }) as any,
        rowVirtualizerOptions: {
            overscan: 10,
            onScroll: (event) => {
                const { scrollOffset, scrollHeight, clientHeight } = event;
                if (scrollOffset + clientHeight >= scrollHeight - 200) {
                    // Load more data when user scrolls to bottom
                    // Implement your data fetching logic here
                }
            },
            // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        } as any,
        enableExpanding: false,
    });

    return <MantineReactTable table={table} />;
};
